<template>
  <FreeBottomSheet :name="name" @onClose="cleanInputValue">
    <template #header="{ hide }">
      <FreeHeader class="text-text-neutral-primary p-4">
        <h2 class="text-base font-medium">
          {{ t('title') }}
        </h2>
        <template #rightAction>
          <FreeCloseButton @click="hide" />
        </template>
      </FreeHeader>
      <FreeSearchInput
        v-if="isSearchInputVisible"
        class="px-4 pb-2"
        :model-value="citizenshipSearchValue"
        :placeholder="t('search')"
        @onInput="updateQuery"
        @onCancel="cleanInputValue"
      >
        <template #prepend>
          <img
            src="../../../assets/images/search.svg"
            width="24"
            height="24"
            alt=""
          />
        </template>
        <template #clearButton>
          <FreeCloseButton />
        </template>
      </FreeSearchInput>
    </template>
    <template #default>
      <div class="min-h-[calc(100vh-232px)] px-4 pb-4">
        <div v-show="!citizenshipSearchValue" class="space-y-3">
          <CountriesList
            v-if="isPopularCountriesVisible"
            :selected-country-code="selectedCountryCode"
            :countries-list="citizenshipEntity.config.popularCountries"
            @onCitizenshipSelect="handleCitizenshipSelect"
          />
          <div class="space-y-3">
            <CountriesList
              v-for="(countriesGroup, key, idx) in groupedCountries"
              :key="idx"
              :group-letter="key"
              :selected-country-code="selectedCountryCode"
              :countries-list="countriesGroup"
              @onCitizenshipSelect="handleCitizenshipSelect"
            />
          </div>
        </div>
        <CountriesList
          v-show="citizenshipSearchValue && !isEmptySearch"
          :selected-country-code="selectedCountryCode"
          :countries-list="matchedCitizenships"
          @onCitizenshipSelect="handleCitizenshipSelect"
        />
        <EmptyResult v-show="citizenshipSearchValue && isEmptySearch" />
      </div>
    </template>
  </FreeBottomSheet>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { appLocale, citizenshipEntity, controllerModel } from 'shared';

  import {
    FreeBottomSheet,
    FreeCloseButton,
    FreeHeader,
    FreeSearchInput,
  } from '../../../shared/ui';
  import EmptyResult from './EmptyResult.vue';
  import CountriesList from './CountriesList.vue';

  const props = withDefaults(
    defineProps<{
      name: string;
      selectedCountryCode: string;
      isSearchInputVisible?: boolean;
      isPopularCountriesVisible?: boolean;
      countryCodes?: string[];
    }>(),
    {
      isSearchInputVisible: true,
      isPopularCountriesVisible: true,
    },
  );

  const emit = defineEmits<{ onCitizenshipSelect: [citizenship: string] }>();

  const { t } = useI18n();
  const bottomSheet = controllerModel.useBottomSheet();
  const locale = appLocale.lib.getAppLocale();

  const groupedCountries = computed(() =>
    citizenshipEntity.lib.groupCountriesByAlphabet(locale, props.countryCodes),
  );

  const citizenshipSearchValue = ref('');

  const matchedCitizenships = computed(() =>
    citizenshipEntity.lib.getMatchingCountries(
      citizenshipSearchValue.value,
      locale,
      props.countryCodes,
    ),
  );

  const isEmptySearch = computed(() => {
    return matchedCitizenships.value.length === 0;
  });

  const updateQuery = (query: string) => (citizenshipSearchValue.value = query);
  const cleanInputValue = () => (citizenshipSearchValue.value = '');

  const handleCitizenshipSelect = (citizenshipCode: string) => {
    bottomSheet.hide(props.name);
    emit('onCitizenshipSelect', citizenshipCode);
  };
</script>

<i18n src="../locales.json"></i18n>
